import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

type GuideSection = {
  id: number;
  title: string;
  content: React.ReactNode;
};

const InstructionGuide: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  
  const toggleGuide = () => {
    setIsOpen(!isOpen);
  };

  const nextSection = () => {
    if (activeSection < guideSections.length - 1) {
      setActiveSection(activeSection + 1);
    } else {
      // Close guide if on last section
      setIsOpen(false);
    }
  };

  const prevSection = () => {
    if (activeSection > 0) {
      setActiveSection(activeSection - 1);
    }
  };
  
  const guideSections: GuideSection[] = [
    {
      id: 1,
      title: t('instructionGuide.purpose.title', 'Purpose of Ramadan'),
      content: (
        <div className="space-y-3">
          <p className="text-gray-800 font-bengali">
            {t('instructionGuide.purpose.content1', 'সিয়াম ও রামাদানের মূল উদ্দেশ্য তাকওয়া অর্জন করা, বদঅভ্যাস পরিত্যাগ করা এবং রামাদান কেন্দ্রিক কুরআন-হাদীসের আমলগুলো যথা নিয়মে করা।')}
          </p>
          <div className="flex justify-center py-2">
            <div className="w-full max-w-md py-4 text-center bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-rose-500 font-medium mb-2 font-bengali">{t('instructionGuide.purpose.allah', 'মহান আল্লাহ বলেন-')}</p>
              <p dir="rtl" lang="ar" className="text-green-700 text-xl my-3 font-arabic leading-loose">
                يَايُّهَا الَّذِينَ آمَنُوا كُتِبَ عَلَيْكُمُ الضِيَامُ كَمَا كُتِبَ عَلَى الَّذِينَ مِنْ قَبْلِكُمْ لَعَلَّكُمْ تَتَّقُوْنَ
              </p>
              <p className="text-gray-800 mx-4 font-bengali">
                {t('instructionGuide.purpose.ayat', 'হে ঈমানদারগণ! তোমাদের উপর সিয়াম ফরয করা হয়েছে, যেভাবে ফরয করা হয়েছিল তোমাদের পূর্ববর্তীদের উপর। যাতে তোমরা তাকওয়া অবলম্বন কর।')}
              </p>
              <p className="text-gray-600 mt-2 text-sm font-bengali">
                {t('instructionGuide.purpose.source', '(সূরা বাকারা, আয়াত: ১৮৩;)')}
              </p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 2,
      title: t('instructionGuide.guidelines.title', 'Ramadan Guidelines'),
      content: (
        <div className="space-y-3 mt-2">
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <p className="text-gray-800 font-bengali mb-3">
              {t('instructionGuide.guidelines.content1', 'রামাদানকে ফলপ্রসূকরার জন্য রামাদান প্ল্যানার তৈরি করা হয়েছে। এটা কুরআন-সুন্নাহ দ্বারা নির্ধারিত কোনো ফর্মুলা নয়। যে কেউ রামাদানকে সার্থক করতে যে কোনোভাবে প্ল্যান করে রামাদানের আমল করতে পারেন')}
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-800 font-bengali">
              <li>{t('instructionGuide.guidelines.point2', 'প্ল্যানারটিতে বেশ কয়েকটি অংশ রয়েছে। যথা- দিনের আয়াত, দিনের হাদীস, দিনের দু\'আ, দৈনিক চেকলিস্ট। এছাড়াও রয়েছে আল্লাহর গুণবাচক নাম ও প্রতিদিনের কাজ।')}</li>
              <li>{t('instructionGuide.guidelines.point3', 'প্রতিদিন একটি করে কুরআনের আয়াত, একটি হাদীস ও একটি দিনের দু\'আ মুখস্থ করার জন্য দেওয়া হয়েছে। মুখস্থ করতে না পারলে কুরআন-হাদীসের উদ্ধৃতি পাঠ করে ইলম হাসিল ও অন্যের সঙ্গে শেয়ার করতে পারেন। আর অর্থসহ দু\'আ পাঠ করে আল্লাহর নিকট প্রার্থনা করুন।')}</li>
              <li>{t('instructionGuide.guidelines.point4', 'কোন্ কাজটা কখন করবেন তা আপনার সময়ানুযায়ী ভাগ করে নিন। কখন আয়াত মুখস্থ করবেন, কখন হাদীস মুখস্থ করবেন, কখন দু\'আ মুখস্থ করবেন তার জন্য আপনার সুবিধামত সময় নির্ধারণ করে নিন। মুখস্থ করার পরিকল্পনার অংশ হিসেবে যা মুখস্থ করবেন তা অন্যের সাথে শেয়ার করুন।')}</li>
              <li>{t('instructionGuide.guidelines.point5', 'দু\'আরজন্য রাহে বেলায়েত, হিসনুল মুসলিম, রাসূল এর সকাল-সন্ধ্যার দু\'আ ও যিক্র বইটিরসহায়তা নিতে পারেন।')}</li>
              <li>{t('instructionGuide.guidelines.point6', 'কুরআন বুঝে পড়ার চেষ্টা করুন। তাদাব্বুর করুন। তাদাব্বুর দ্বারা উদ্দেশ্য হলো, মর্ম অনুধাবনের চেষ্টা করা।')}</li>
              <li>{t('instructionGuide.guidelines.point7', 'যারা কুরআন পড়তে পারেন না, তারা সম্পূর্ণ মনোযোগের সাথে তিলাওয়াত শ্রবণ করুন।')}</li>
              <li>{t('instructionGuide.guidelines.point8', 'প্রতিদিন কুরআনুল কারীমের কিছু না কিছু অংশ বুঝে পড়ার চেষ্টা করুন।')}</li>
              <li>{t('instructionGuide.guidelines.point9', 'আল্লাহর গুণবাচক নামসমূহের প্রতিবিশ্বাস স্থাপন করা ঈমানের অংশ। অতএব প্রতিদিন কমপক্ষে আল্লাহর তিনটি গুণবাচক নাম ও তার অর্থ মুখস্থ করুন ও অনুধাবন করুন। রাসূল বলেন, "নিশ্চয়ই আল্লাহর নিরানব্বইটি নাম আছে। যে ব্যক্তি সে নামগুলো মুখস্থ করবে (উপলব্ধি করবে ও ঈমান আনবে), সে ব্যক্তি জান্নাতে প্রবেশ করবে। (মুসলিম-২৬৭৭, বুখারী-২৭৩৬)')}</li>
              <li>{t('instructionGuide.guidelines.point10', 'পরের দিনের কাজের তালিকা আগের দিন দেখে নিন। আগামীকাল আপনার জন্য কী কী চ্যালেঞ্জ রয়েছে, তা আজই দেখে নিন এবং সে অনুসারে পরিকল্পনা করে আপনার দিনের আমলগুলো করে নিন। কোনো আমল সময়মত করতে না পারলে পরবর্তী সময়ে এমনকি পরবর্তী দিনে হলেও করে নিন।')}</li>
              <li>{t('instructionGuide.guidelines.point11', 'ইফতারের সময় দু\'আ করুন এবং আযানের জবাব দিন।')}</li>
              <li>{t('instructionGuide.guidelines.point12', 'টাইম কিলার ইউটিউব ও সামাজিক যোগাযোগ মাধ্যমে সময় কমানোর পরিকল্পনা করুন। আপনি সারাদিন কতক্ষণ এবং কোন্ সময় দীনী উদ্দেশ্যে সামাজিক যোগাযোগ মাধ্যম ব্যবহার করবেন তা নির্ধারণ করুন। পারলে রামাদান মাসে সামাজিক যোগাযোগ মাধ্যম থেকে নিজেকে সরিয়ে রাখুন। আর শা\'বান মাসেই পরীক্ষা করুন কতক্ষণ সময় আপনি এখাতে ব্যয় করবেন।')}</li>
              <li>{t('instructionGuide.guidelines.point13', 'দৈনিক চেকলিস্টের কাজগুলো নিয়মিত করার চেষ্টা করুন। যে কাজগুলো করেছেন দিন শেষে সেগুলোতে টিক দিন।')}</li>
              <li>{t('instructionGuide.guidelines.point14', 'দিনের কাজটি মনযোগ দিয়ে করুন।')}</li>
              <li>{t('instructionGuide.guidelines.point15', 'রামাদানের শেষ মুহূর্ত অনেক গুরুত্বপূর্ণ। ঈদের কেনাকাটায় তা যেন নষ্ট না হয়। এ জন্য কিছু কেনাকাটা থাকলে রামাদান শুরুর আগেই করে ফেলুন।')}</li>
              <li>{t('instructionGuide.guidelines.point16', 'পরিবারের সদস্যগণ প্ল্যানার পূরণে একে অন্যের সঙ্গে প্রতিযোগিতায় অবতীর্ণ হোন। স্বয়ং আল্লাহ ভালো কাজে প্রতিযোগিতার নির্দেশ করেছেন।')}</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: 3,
      title: t('instructionGuide.dosAndDonts.title', 'Dos and Don\'ts'),
      content: (
        <div className="mt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
              <h3 className="text-center text-emerald-700 font-medium pb-2 border-b border-emerald-200 mb-3 font-bengali">
                {t('instructionGuide.dosAndDonts.do', 'করণীয়')}
              </h3>
              <ul className="list-disc list-inside space-y-1 text-gray-800 text-sm font-bengali">
                <li>{t('instructionGuide.dosAndDonts.do1', 'আল্লাহ তাআলার উপর আস্থা রাখুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.do2', 'সালাত সুন্দর করে ধীরস্থিরভাবে আদায় করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.do3', 'কুরআন পড়ুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.do4', 'প্রত্যেক ভালো কাজ আল্লাহর নামে আরম্ভ করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.do5', 'আপনার সম্পদের হিসেব করে যাকাত প্রদান করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.do6', 'গোপনে দান করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.do7', 'নিকট আত্মীয়দের জন্য ব্যয় করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.do8', 'অভাবীদের জন্য ব্যয় করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.do9', 'আপনার সিয়ামকে অর্থবহ করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.do10', 'চোখের পর্দা করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.do11', 'অন্যকে সৎকর্মের দিকে উৎসাহিত এবং পরিচালিত করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.do12', 'দিনমজুর ও সাধারণ শ্রেণীর মানুষদের সালাম দিন, শ্রদ্ধা করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.do13', 'অধীনস্থদের প্রতি সদয় থাকুন। তাদের কাজের বোঝা হালকা করে দিন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.do14', 'স্বাস্থ্যসম্মত ইফতার করুন এবং অতিভোজন পরিহার করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.do15', 'ভোর রাতে আল্লাহর নিকট দু\'আ করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.do16', 'সাহরীতে একটি হলেও খেজুর খান।')}</li>
                <li>{t('instructionGuide.dosAndDonts.do17', 'পথে কষ্টদায়ক কিছু দেখলে তা পরিষ্কার করুন।')}</li>
              </ul>
            </div>
            
            <div className="bg-rose-50 rounded-lg p-4 border border-rose-200">
              <h3 className="text-center text-rose-700 font-medium pb-2 border-b border-rose-200 mb-3 font-bengali">
                {t('instructionGuide.dosAndDonts.dont', 'বর্জনীয়')}
              </h3>
              <ul className="list-disc list-inside space-y-1 text-gray-800 text-sm font-bengali">
                <li>{t('instructionGuide.dosAndDonts.dont1', 'অযথা বা অর্থহীন কাজ পরিহার করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.dont2', 'টিভি সিরিয়াল, মুভি, নাটক, সিনেমা দেখা পরিহার করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.dont3', 'হারাম উপার্জন বন্ধ করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.dont4', 'আপনি সঠিক হওয়া সত্ত্বেও পরিবার কিংবা পরিবারের বাইরে কারো সাথে তর্ক পরিহার করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.dont5', 'ঝগড়া পরিহার করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.dont6', 'মিথ্যা কথা ও কাজ পরিহার করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.dont7', 'যে কোনো পাপকাজ পরিহার করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.dont8', 'কারো ব্যাপারে সন্দেহ বা কুধারণা করবেন না।')}</li>
                <li>{t('instructionGuide.dosAndDonts.dont9', 'কারো পেছনে সমালোচনা করবেন না।')}</li>
                <li>{t('instructionGuide.dosAndDonts.dont10', 'কারো প্রতি রূঢ় আচরণ করবেন না।')}</li>
                <li>{t('instructionGuide.dosAndDonts.dont11', 'কারো প্রতি হিংসা বা শত্রুতা লালন করবেন না।')}</li>
                <li>{t('instructionGuide.dosAndDonts.dont12', 'মিউজিক শুনবেন না।')}</li>
                <li>{t('instructionGuide.dosAndDonts.dont13', 'গেমস খেলে সময় নষ্ট করবেন না।')}</li>
                <li>{t('instructionGuide.dosAndDonts.dont14', 'কারো কাছ থেকে অবৈধ কোনো সুবিধা নিবেন না বা দিবেন না।')}</li>
                <li>{t('instructionGuide.dosAndDonts.dont15', 'আড্ডা ও দল বেঁধে গল্পগুজব পরিহার করুন।')}</li>
                <li>{t('instructionGuide.dosAndDonts.dont16', 'কারো মনে কষ্ট হয় এমন কোনো আচরণ করবেন না।')}</li>
                <li>{t('instructionGuide.dosAndDonts.dont17', 'সাহরী, ইফতার ও ঈদের কেনাকাটায় অপচয় করবেন না।')}</li>
              </ul>
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-gray-800 text-center font-bengali">
              {t('instructionGuide.dosAndDonts.quote', 'সিয়াম পালনের মাধ্যমে হারাম কাজ ও খারাপ অভ্যাস ত্যাগ করতে পারলে আপনার রামাদান সাধনা অনেকাংশেই সফল, ইন শা আল্লাহ')}
            </p>
            <div className="text-center mt-3">
              <p className="text-emerald-700 font-medium font-bengali">{t('instructionGuide.dosAndDonts.prophet', 'রাসূলুল্লাহ ﷺ বলেছেন-')}</p>
              <p className="text-gray-800 mt-2 italic font-bengali">
                {t('instructionGuide.dosAndDonts.hadith', 'যে ব্যক্তি মিথ্যা কথা ও কাজ বর্জন করেনি, তার এ পানাহার পরিত্যাগ করায় আল্লাহর কোনো প্রয়োজন নেই।')}
              </p>
              <p className="text-gray-600 text-sm mt-1 font-bengali">
                {t('instructionGuide.dosAndDonts.source', '(সহীহ বুখারী, হাদীস: ১৯০৩)')}
              </p>
            </div>
          </div>
        </div>
      ),
    },
  ];
  
  return (
    <>
      <button
        onClick={toggleGuide}
        className="flex items-center text-sm bg-primary text-white hover:bg-primary-dark focus:outline-none px-4 py-2 rounded-md shadow-sm transition-colors font-bengali"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 mr-1"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        {t('instructionGuide.viewGuide', 'রামাদান প্ল্যানার নির্দেশিকা')}
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="guide-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen px-4 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
              onClick={toggleGuide}
            ></div>

            {/* Guide modal */}
            <div 
              className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full"
              style={{
                backgroundImage: 'url("/images/bg-pattern.png")',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {/* Decorative corners */}
              <div className="absolute top-0 left-0 w-24 h-24 opacity-50">
                <img src="/images/corner-tl.png" alt="" className="w-full h-full" />
              </div>
              <div className="absolute top-0 right-0 w-24 h-24 opacity-50 transform rotate-90">
                <img src="/images/corner-tl.png" alt="" className="w-full h-full" />
              </div>
              <div className="absolute bottom-0 left-0 w-24 h-24 opacity-50 transform -rotate-90">
                <img src="/images/corner-tl.png" alt="" className="w-full h-full" />
              </div>
              <div className="absolute bottom-0 right-0 w-24 h-24 opacity-50 transform rotate-180">
                <img src="/images/corner-tl.png" alt="" className="w-full h-full" />
              </div>
              
              <div className="relative bg-white bg-opacity-90 p-6 sm:p-10 font-bengali">
                {/* Header */}
                <div className="flex justify-between items-center border-b border-amber-200 pb-4 mb-6">
                  <h3 className="text-2xl leading-6 font-medium text-primary">
                    {guideSections[activeSection].title}
                  </h3>
                  <button
                    onClick={toggleGuide}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">{t('instructionGuide.close', 'Close')}</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Content */}
                <div className="mb-8 overflow-y-auto max-h-[60vh]">
                  {guideSections[activeSection].content}
                </div>
                
                {/* Navigation */}
                <div className="flex justify-between items-center border-t border-amber-200 pt-4">
                  <div>
                    {activeSection > 0 && (
                      <button
                        onClick={prevSection}
                        className="inline-flex items-center px-4 py-2 border border-amber-300 text-sm font-medium rounded-md text-amber-700 bg-amber-50 hover:bg-amber-100"
                      >
                        <svg className="mr-2 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {t('instructionGuide.previous', 'Previous')}
                      </button>
                    )}
                  </div>
                  
                  <div className="flex space-x-1">
                    {guideSections.map((_, index) => (
                      <span
                        key={index}
                        className={`w-2 h-2 rounded-full ${
                          index === activeSection ? 'bg-primary' : 'bg-gray-300'
                        }`}
                      ></span>
                    ))}
                  </div>
                  
                  <div>
                    {activeSection < guideSections.length - 1 ? (
                      <button
                        onClick={nextSection}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark"
                      >
                        {t('instructionGuide.next', 'Next')}
                        <svg className="ml-2 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    ) : (
                      <button
                        onClick={toggleGuide}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark"
                      >
                        {t('instructionGuide.close', 'Close')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InstructionGuide; 